import React, { useEffect, useState } from "react";
import { X, ChevronDown, UserCheck, Calendar, Wallet } from "lucide-react";

import { taskAPI } from "../../../services/api";
import { useWallet } from "../../../context/WalletContext";
import { ModelWrapper } from "../ModelWrapper";
import { PERMISSION_TYPES } from "../kanban-board";

import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from "@headlessui/react";

export function TaskFormModal({
  project,
  initialTask,
  open,
  onClose,
  onSave,
  innovators,
  isConnected: isConnectedProp,
}) {
  // Use the prop if provided, otherwise fall back to context
  const { walletAddress, isConnected: isConnectedContext } = useWallet();
  const isConnected = isConnectedProp !== undefined ? isConnectedProp : isConnectedContext;
  
  const isEdit = Boolean(initialTask);
  
  // Check if wallet is connected before filtering innovators
  const otherInnovators = isConnected && walletAddress ? 
    innovators.filter((i) => i.walletAddress.toLowerCase() !== walletAddress.toLowerCase()) : 
    innovators;
    
  const [form, setForm] = useState({
    title: initialTask?.title || "",
    permissionType: initialTask?.permissionType || "application",
    description: initialTask?.description || "",
    taskPoints: initialTask?.taskPoints || 1,
    dueDate: initialTask?.dueDate ? initialTask.dueDate.split("T")[0] : "",
    assignee: initialTask?.assigneeWalletAddress || null,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  /* auto-assign toggle */
  useEffect(() => {
    if (form.permissionType === "claim" && isConnected && walletAddress)
      setForm((p) => ({ ...p, assignee: walletAddress }));
    else if (form.permissionType !== "assign")
      setForm((p) => ({ ...p, assignee: null }));
  }, [form.permissionType, walletAddress, isConnected]);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  async function handleSubmit(e) {
    e.preventDefault();
    
    // Check if wallet is connected
    if (!isConnected || !walletAddress) {
      return setError("Please connect your wallet to create or edit tasks.");
    }
    
    if (
      !form.title ||
      !form.permissionType ||
      !form.description ||
      form.taskPoints <= 0
    )
      return setError("Fill in all required fields.");
    if (form.permissionType === "assign" && !form.assignee)
      return setError("Select an assignee.");

    try {
      setLoading(true);
      const payload = {
        ...form,
        walletAddress,
        projectId: project._id,
        taskPoints: Number(form.taskPoints),
      };
      const { data } = isEdit
        ? await taskAPI.update(initialTask._id, payload)
        : await taskAPI.create(payload);

      onSave(data);
      setForm({
        title: "",
        permissionType: "application",
        description: "",
        taskPoints: 1,
        dueDate: "",
        assignee: null,
      })
      onClose();
    } catch (err) {
      setError(err.message || "Error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModelWrapper show={open} onClose={onClose}>
      <header className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {isEdit ? "Edit" : "Create"} Task
        </h3>
        <button onClick={onClose}>
          <X className="h-5 w-5" />
        </button>
      </header>

      {/* Wallet connection notice */}
      {(!isConnected || !walletAddress) && (
        <div className="mb-4 flex items-start gap-2 rounded bg-amber-50 p-3 text-sm text-amber-800">
          <Wallet className="h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-medium">Wallet connection required</p>
            <p className="text-xs mt-1">Please connect your wallet to create or edit tasks.</p>
          </div>
        </div>
      )}

      {error && (
        <p className="mb-4 rounded bg-red-50 p-2 text-xs text-red-600">
          {error}
        </p>
      )}

      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label className="mb-1 block text-xs font-medium">Title *</label>
          <input
            name="title"
            value={form.title}
            onChange={handleChange}
            className="w-full rounded border p-2 text-sm"
            required
            disabled={!isConnected || !walletAddress}
          />
        </div>

        {/* permissions radio grid */}
        <div>
          <label className="mb-1 block text-xs font-medium">Permission *</label>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {Object.entries(PERMISSION_TYPES).map(([id, p]) => (
              <label
                key={id}
                className={`flex cursor-pointer items-start gap-2 rounded border p-2 text-xs ${
                  form.permissionType === id
                    ? "border-indigo-500 bg-indigo-50"
                    : "hover:bg-gray-50"
                } ${(!isConnected || !walletAddress) ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <input
                  type="radio"
                  checked={form.permissionType === id}
                  onChange={() => setForm({ ...form, permissionType: id })}
                  disabled={!isConnected || !walletAddress}
                />
                <span>
                  <span className="font-medium">{p.label}</span>
                  <span className="block text-[10px] text-gray-500">
                    {p.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* assignee radio list (assign only) */}
        {form.permissionType === "assign" && (
          <div>
            <label className="mb-1 block text-xs font-medium">
              Assign To *
            </label>
            {otherInnovators.length === 0 ? (
              <p className="rounded bg-amber-50 p-2 text-[10px] text-amber-700">
                No innovation yet.
              </p>
            ) : (
              <Listbox
                name="assignee"
                value={
                  otherInnovators.find(
                    (c) => c.walletAddress === form.assignee
                  ) || otherInnovators[0]
                }
                onChange={(opt) => {
                  setForm({ ...form, assignee: opt.walletAddress });
                }}
                disabled={!isConnected || !walletAddress}
              >
                <div className="relative mt-1">
                  {/* button */}
                  <ListboxButton className={`flex w-full items-center justify-between rounded border border-zinc-600 px-3 py-1 text-left text-xs ${(!isConnected || !walletAddress) ? "opacity-50 cursor-not-allowed" : ""}`}>
                    {({ value }) => (
                      <>
                        <span className="flex items-center gap-2">
                          {value.avatar && (
                            <img
                              src={value.avatar}
                              alt=""
                              className="h-4 w-4 rounded-full"
                            />
                          )}
                          {value.name || value.walletAddress.slice(0, 8) + "…"}
                        </span>
                        <ChevronDown className="h-4 w-4 text-zinc-400" />
                      </>
                    )}
                  </ListboxButton>

                  {/* options */}
                  <ListboxOptions className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded bg-white text-xs shadow-xl ring-1 ring-black/5 focus:outline-none">
                    {otherInnovators.map((c) => (
                      <ListboxOption
                        key={c.walletAddress}
                        value={c}
                        className="group cursor-pointer select-none py-2 pl-3 pr-8 text-gray-900 data-focus:bg-indigo-600 data-focus:text-white"
                      >
                        {({ selected }) => (
                          <span
                            className={`flex items-center gap-2 ${
                              selected ? "font-semibold" : ""
                            }`}
                          >
                            {c.avatar && (
                              <img
                                src={c.avatar}
                                alt=""
                                className="h-4 w-4 rounded-full"
                              />
                            )}
                            {c.name || c.walletAddress.slice(0, 8) + "…"}
                          </span>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            )}
          </div>
        )}

        {form.permissionType === "claim" && (
          <p className="flex items-start gap-2 rounded border border-green-200 bg-green-50 p-2 text-[10px] text-green-700">
            <UserCheck className="h-4 w-4" />
            Task will be auto-assigned to you when claimed.
          </p>
        )}

        <div>
          <label className="mb-1 block text-xs font-medium">
            Description *
          </label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={4}
            className="w-full rounded border p-2 text-sm"
            required
            disabled={!isConnected || !walletAddress}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium">
              Task Points (IU) *
            </label>
            <input
              name="taskPoints"
              type="number"
              min={1}
              value={form.taskPoints}
              onChange={handleChange}
              className="w-full rounded border p-2 text-sm"
              disabled={!isConnected || !walletAddress}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium">Due Date</label>
            <div className="flex rounded border">
              <span className="flex items-center border-r bg-gray-50 px-2">
                <Calendar className="h-4 w-4 text-gray-500" />
              </span>
              <input
                name="dueDate"
                type="date"
                value={form.dueDate}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                className="w-full p-2 text-sm"
                disabled={!isConnected || !walletAddress}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1 text-xs hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !isConnected || !walletAddress}
            className="rounded bg-indigo-600 px-3 py-1 text-xs text-white disabled:opacity-50"
          >
            {loading
              ? "Saving..."
              : isEdit
              ? "Save Changes"
              : "Create Task"}
          </button>
        </div>
      </form>
    </ModelWrapper>
  );
}
